import type { Direction } from "../avatars.js";
import type { WorkstationType } from "../roles.js";

export interface Vec2 {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DeskDef {
  id: string;
  workstationType: WorkstationType;
  /** Where the player sits (feet position), in pixels. */
  seat: Vec2;
  /** Where a player must stand to start working, in pixels. */
  interact: Vec2;
  /** Facing direction the avatar snaps to while seated. */
  facing: Direction;
}

export interface ZoneDef {
  id: string;
  type: string;
  /** `rect` zones use x/y/width/height; `polygon` zones use points. */
  shape: "rect" | "polygon";
  rect?: Rect;
  points?: Vec2[];
}

export type PointLightDef = Vec2 & {
  id: string;
  kind: "desk" | "screen" | "lamp";
  /** Desk and screen markers share the numbered desk's occupancy. */
  deskId?: string;
};

export interface WindowLightDef {
  id: string;
  rect: Rect;
  direction: "up" | "down";
}

export interface MapData {
  width: number;
  height: number;
  tileSize: number;
  /** `width * height` cells; 1 = blocked, 0 = walkable. */
  collision: Uint8Array;
  /**
   * Sub-tile colliders (map pixels) from the collision shapes drawn in Tiled's
   * tile collision editor, indexed by every cell each rectangle overlaps.
   * Cells whose colliding tile has no shapes are blocked whole in `collision`.
   */
  shapeColliders: Map<number, Rect[]>;
  /** Solid tiles in the walls layer; furniture does not block light. */
  lightBlockers: Uint8Array;
  desks: DeskDef[];
  relaxZones: ZoneDef[];
  exitZone: ZoneDef;
  spawns: Vec2[];
  coffeeMachines: Vec2[];
  pointLights: PointLightDef[];
  windowLights: WindowLightDef[];
  /** Local-player trigger for revealing the bathroom beneath its ceiling. */
  bathroomArea?: ZoneDef;
  /** Where players control the hi-fi. Maps without one have no radio. */
  radioArea?: ZoneDef;
  /** Centre of `radioArea`: the radio's sound source. */
  radioPos?: Vec2;
  /** Names of the render layers, in draw order (client use). */
  layers: string[];
}

export function isWalkableTile(map: MapData, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) return false;
  return map.collision[ty * map.width + tx] === 0;
}
